import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    Divider
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useTranslation } from 'react-i18next';
import { theme } from '../../../theme';
import SalonProposalExpiryField from '../../pro/salon/SalonProposalExpiryField';

const DAYS_OF_WEEK = [
    { id: 'lundi', label: 'Lundi' },
    { id: 'mardi', label: 'Mardi' },
    { id: 'mercredi', label: 'Mercredi' },
    { id: 'jeudi', label: 'Jeudi' },
    { id: 'vendredi', label: 'Vendredi' },
    { id: 'samedi', label: 'Samedi' },
    { id: 'dimanche', label: 'Dimanche' },
];

const generateTimeSlots = () => {
    const slots = [];
    for (let h = 0; h < 24; h++) {
        for (let m = 0; m < 60; m += 15) {
            const hour = h.toString().padStart(2, '0');
            const minute = m.toString().padStart(2, '0');
            slots.push(`${hour}:${minute}`);
        }
    }
    return slots;
};

const TIME_SLOTS = generateTimeSlots();

const SalonOverview = ({ salon, onUpdate }) => {
    const { t } = useTranslation();

    const [scheduleOpen, setScheduleOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [proposalExpiry, setProposalExpiry] = useState(salon.appointment_proposal_default_expiry_hours || 24);
    const [savingProposalExpiry, setSavingProposalExpiry] = useState(false);

    useEffect(() => {
        setProposalExpiry(salon.appointment_proposal_default_expiry_hours || 24);
    }, [salon.appointment_proposal_default_expiry_hours]);

    const proposalExpiryIsValid = Number.isInteger(proposalExpiry)
        && proposalExpiry >= 1
        && proposalExpiry <= 720;

    const handleSaveProposalExpiry = async () => {
        if (!proposalExpiryIsValid) return;

        setSavingProposalExpiry(true);
        try {
            await onUpdate(salon.salon_id, {
                appointment_proposal_default_expiry_hours: proposalExpiry,
            });
        } catch (error) {
            console.error(error);
        } finally {
            setSavingProposalExpiry(false);
        }
    };

    // Normalize opening hours
    const getInitialHours = () => {
        let hours = salon.opening_hours || {};

        // Handle cases where opening_hours might be a string (double serialization)
        if (typeof hours === 'string') {
            try {
                hours = JSON.parse(hours);
                console.log("[DEBUG] Parsed opening_hours from string:", hours);
            } catch (e) {
                console.error("[DEBUG] Failed to parse opening_hours string:", e);
                hours = {};
            }
        }

        const normalized = {};

        DAYS_OF_WEEK.forEach(day => {
            if (hours[day.id]) {
                normalized[day.id] = { ...hours[day.id] };
            } else {
                normalized[day.id] = { ouverture: '09:00', fermeture: '18:00' };
            }
        });
        return normalized;
    };

    const [editHours, setEditHours] = useState(getInitialHours());

    const handleOpenSchedule = () => {
        setEditHours(getInitialHours());
        setScheduleOpen(true);
    };

    const handleSaveSchedule = async () => {
        setSaving(true);
        try {
            await onUpdate(salon.salon_id, { opening_hours: editHours });
            setScheduleOpen(false);
        } catch (error) {
            console.error(error);
        } finally {
            setSaving(false);
        }
    };

    const handleDayChange = (dayId, field, value) => {
        setEditHours(prev => ({
            ...prev,
            [dayId]: {
                ...prev[dayId],
                [field]: value
            }
        }));
    };

    const toggleDayClosed = (dayId) => {
        setEditHours(prev => {
            const current = prev[dayId];
            const isClosed = current.ouverture === 'fermé' || current.ouverture === 'Fermé';

            return {
                ...prev,
                [dayId]: {
                    ouverture: isClosed ? '09:00' : 'fermé',
                    fermeture: isClosed ? '18:00' : 'fermé'
                }
            };
        });
    };

    const isClosed = (hours) => {
        if (!hours) return true;
        const open = String(hours.ouverture || '').toLowerCase();
        return open === 'fermé' || open === '' || open === 'closed';
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: { xs: 2, md: 3 }, flexWrap: 'wrap' }}>
                {/* Info Card */}
                <Paper sx={{ flex: '1 1 300px', p: { xs: 2, md: 3 }, borderRadius: theme.borderRadius.lg, border: `1px solid ${theme.colors.border.light}`, boxShadow: theme.boxShadow.sm, width: '100%' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>Informations Clés</Typography>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ color: theme.colors.text.tertiary, textTransform: 'uppercase', fontWeight: 700 }}>Responsable</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{salon.owner?.first_name} {salon.owner?.last_name}</Typography>
                        <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>{salon.owner?.email}</Typography>
                        <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>{salon.owner?.phone}</Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box>
                        <Typography variant="caption" sx={{ color: theme.colors.text.tertiary, textTransform: 'uppercase', fontWeight: 700 }}>Activité</Typography>
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            <Grid item xs={4}>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: theme.colors.primary[500], fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                                    {salon.employees_count > 0 ? salon.employees_count : t('superadmin.overview.no_staff')}
                                </Typography>
                                <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                                    {t('superadmin.overview.staff')}
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: theme.colors.secondary[500], fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                                    {salon.appointments_count > 0 ? salon.appointments_count : t('superadmin.overview.no_appointments')}
                                </Typography>
                                <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                                    {t('superadmin.overview.appointments')}
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: theme.colors.success[500], fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                                    {salon.clients_count > 0 ? salon.clients_count : t('superadmin.overview.no_clients')}
                                </Typography>
                                <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                                    {t('superadmin.overview.clients')}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box>
                        <Typography variant="caption" sx={{ color: theme.colors.text.tertiary, textTransform: 'uppercase', fontWeight: 700 }}>
                            Propositions de rendez-vous
                        </Typography>
                        <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <SalonProposalExpiryField
                                value={proposalExpiry}
                                onChange={setProposalExpiry}
                                label="Expiration des propositions (heures)"
                                helperText={proposalExpiryIsValid ? 'Entre 1 et 720 heures.' : 'Choisissez une durée entre 1 et 720 heures.'}
                                disabled={savingProposalExpiry}
                            />
                            <Box>
                                <Button
                                    variant="contained"
                                    onClick={handleSaveProposalExpiry}
                                    disabled={!proposalExpiryIsValid || savingProposalExpiry}
                                    sx={{ fontWeight: 700 }}
                                >
                                    {savingProposalExpiry ? 'Enregistrement...' : 'Enregistrer le délai'}
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Paper>

                {/* Schedule Card */}
                <Paper sx={{ flex: '1 1 400px', p: { xs: 2, md: 3 }, borderRadius: theme.borderRadius.lg, border: `1px solid ${theme.colors.border.light}`, boxShadow: theme.boxShadow.sm, width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccessTimeIcon color="action" /> Horaires d'ouverture
                        </Typography>
                        <Button
                            variant="outlined"
                            startIcon={<EditIcon />}
                            size="small"
                            onClick={handleOpenSchedule}
                            sx={{ borderRadius: theme.borderRadius.lg, fontWeight: 600 }}
                        >
                            Modifier
                        </Button>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {DAYS_OF_WEEK.map((day) => {
                            let hours = (salon.opening_hours || {})[day.id];

                            // Robust handling for display
                            if (typeof salon.opening_hours === 'string') {
                                try {
                                    const parsed = JSON.parse(salon.opening_hours);
                                    hours = parsed[day.id];
                                } catch (e) { }
                            }

                            const closed = isClosed(hours);

                            return (
                                <Box key={day.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, borderBottom: '1px dashed #eee' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, width: 100 }}>{day.label}</Typography>
                                    <Typography variant="body2" sx={{
                                        color: closed ? theme.colors.error[500] : theme.colors.text.primary,
                                        fontWeight: closed ? 500 : 400
                                    }}>
                                        {closed ? 'Fermé' : `${hours.ouverture} - ${hours.fermeture}`}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Paper>
            </Box>

            {/* Edit Schedule Dialog */}
            <Dialog
                open={scheduleOpen}
                onClose={() => setScheduleOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 800 }}>Modifier les horaires</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        {DAYS_OF_WEEK.map((day) => {
                            const hours = editHours[day.id];
                            const closed = isClosed(hours);

                            return (
                                <Grid item xs={12} key={day.id}>
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        alignItems: { xs: 'flex-start', sm: 'center' },
                                        gap: 2,
                                        p: 2,
                                        bgcolor: closed ? theme.colors.background.secondary : 'white',
                                        borderRadius: theme.borderRadius.md,
                                        border: `1px solid ${theme.colors.border.light}`
                                    }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={!closed}
                                                    onChange={() => toggleDayClosed(day.id)}
                                                    color="success"
                                                />
                                            }
                                            label={<Typography sx={{ fontWeight: 700, width: 80 }}>{day.label}</Typography>}
                                        />

                                        {!closed && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', sm: 'auto' }, justifyContent: 'space-between' }}>
                                                <FormControl size="small" sx={{ width: { xs: '45%', sm: 120 } }}>
                                                    <InputLabel>Ouverture</InputLabel>
                                                    <Select
                                                        value={hours.ouverture}
                                                        label="Ouverture"
                                                        onChange={(e) => handleDayChange(day.id, 'ouverture', e.target.value)}
                                                    >
                                                        {TIME_SLOTS.map(time => (
                                                            <MenuItem key={`start-${time}`} value={time}>{time}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                                <Typography>-</Typography>
                                                <FormControl size="small" sx={{ width: 120 }}>
                                                    <InputLabel>Fermeture</InputLabel>
                                                    <Select
                                                        value={hours.fermeture}
                                                        label="Fermeture"
                                                        onChange={(e) => handleDayChange(day.id, 'fermeture', e.target.value)}
                                                    >
                                                        {TIME_SLOTS.map(time => (
                                                            <MenuItem key={`end-${time}`} value={time}>{time}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                        )}
                                        {closed && <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>Fermé toute la journee</Typography>}
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setScheduleOpen(false)} sx={{ fontWeight: 700 }}>Annuler</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveSchedule}
                        disabled={saving}
                        sx={{ fontWeight: 800 }}
                    >
                        {saving ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SalonOverview;
